import { useCallback } from 'react';
import { addEdge, reconnectEdge } from '@xyflow/react';
import useAutomationsStore from '../../../../store/useAutomationsStore';

export default function useWorkflowEditor() {
  const { nodes, edges, setNodes, setEdges, selectedNode, setSelectedNode } = useAutomationsStore();

  const onNodesChange = useCallback(
    (changes) => {
      let newSelectedNode = undefined; // undefined = no change, null = deselect
      const removedIds = [];

      setNodes((nds) => {
        const updated = [...nds];
        for (const change of changes) {
          const idx = updated.findIndex((n) => n.id === change.id);

          switch (change.type) {
            case 'position':
              if (change.position && idx !== -1) {
                updated[idx] = { ...updated[idx], position: change.position };
              }
              break;
            case 'select':
              if (idx !== -1) {
                updated[idx] = { ...updated[idx], selected: change.selected };
                if (change.selected) {
                  newSelectedNode = updated[idx];
                } else if (newSelectedNode === undefined) {
                  newSelectedNode = null;
                }
              }
              break;
            case 'remove':
              if (idx !== -1) {
                removedIds.push(updated[idx].id);
                updated.splice(idx, 1);
              }
              break;
            case 'dimensions':
              if (change.dimensions && idx !== -1) {
                updated[idx] = {
                  ...updated[idx],
                  measured: { width: change.dimensions.width, height: change.dimensions.height },
                };
              }
              break;
          }
        }
        return updated;
      });

      // Handle selection after state update
      if (newSelectedNode !== undefined) {
        setSelectedNode(newSelectedNode);
      }

      // Clean up edges and selection for removed nodes
      if (removedIds.length > 0) {
        setEdges((eds) => eds.filter((e) => !removedIds.includes(e.source) && !removedIds.includes(e.target)));
        if (removedIds.includes(selectedNode?.id)) {
          setSelectedNode(null);
        }
      }
    },
    [selectedNode?.id, setNodes, setEdges, setSelectedNode]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => {
        let updated = [...eds];
        for (const change of changes) {
          if (change.type === 'remove') {
            updated = updated.filter((e) => e.id !== change.id);
          }
          if (change.type === 'select') {
            updated = updated.map((e) =>
              e.id === change.id ? { ...e, selected: change.selected } : e
            );
          }
        }
        return updated;
      });
    },
    [setEdges]
  );

  const onConnect = useCallback(
    (params) => {
      // Triggers can only connect to one node
      const sourceNode = nodes.find((n) => n.id === params.source);
      if (sourceNode?.type === 'triggerNode') {
        const existingEdge = edges.find((e) => e.source === params.source);
        if (existingEdge) return;
      }

      setEdges((eds) =>
        addEdge(
          { ...params, animated: true, style: { stroke: '#94a3b8', strokeWidth: 2 } },
          eds
        )
      );
    },
    [nodes, edges, setEdges]
  );

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      setEdges((eds) => reconnectEdge(oldEdge, newConnection, eds));
    },
    [setEdges]
  );

  const onEdgeDoubleClick = useCallback(
    (_, edge) => {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    },
    [setEdges]
  );

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onReconnect,
    onEdgeDoubleClick,
  };
}
